<template>
  <Modal
    title="Keisti automobilį ir vairuotoją"
    :is-open="true"
    size="wide"
    @close="closeModal"
  >
    <div class="space-y-4">
      <Alert
        v-if="submitError"
        :text="submitError"
        alert-class="alert-error"
        icon="warning"
        :dismissible="true"
      />
      <Alert
        v-if="overlapingTravels?.hasConflict"
        :text="overlapingTravels.message"
        alert-class="alert-warning"
        icon="warning"
        :dismissible="true"
      />

      <div v-if="isLoadingVehicles" class="text-center py-4">
        Ieškoma laisvų transporto priemonių...
      </div>
      <VehicleSelector
        v-else
        :available-vehicles="availableVehicles"
        :selected-vehicle-id="selectedVehicle?.id"
        @select-vehicle="handleVehicleSelection"
      />

      <div v-if="selectedVehicle?.id">
        <div v-if="isLoadingDrivers" class="text-center py-4">
          Ieškoma laisvų vairuotojų...
        </div>
        <DriverSelector
          v-else
          :available-drivers="availableDrivers"
          :selected-driver-id="selectedDriver?.id"
          @select-driver="handleDriverSelection"
        />
      </div>
    </div>

    <template #footer>
      <button
        @click="submit"
        class="btn btn-primary mr-2"
        :disabled="!selectedVehicle?.id || !selectedDriver?.id || isSubmitting"
      >
        Atnaujinti
      </button>
      <button
        @click="closeModal"
        class="btn btn-ghost"
        :disabled="isSubmitting"
      >
        Atšaukti
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted } from "vue";
import { useTravelStore } from "@/stores/travels";
import { useVehicleStore } from "@/stores/vehicles";
import { useDriverStore } from "@/stores/drivers";

import Modal from "@/components/ui/modals/GeneralModal.vue";
import Alert from "@/components/ui/alerts/DefaultAlert.vue";
import VehicleSelector from "@/components/vehicles/VehicleSelector.vue";
import DriverSelector from "@/components/drivers/DriverSelector.vue";

const props = defineProps({
  travelId: {
    type: [String, Number],
    required: true,
  },
  vehicleId: {
    type: [String, Number],
    required: true,
  },
  driverId: {
    type: [String, Number],
    required: true,
  },
  date: {
    type: String,
    required: true,
  },
  startTime: {
    type: String,
    required: true,
  },
  finishTime: {
    type: String,
    required: true,
  },
  passengersRegularCount: {
    type: Number,
    required: true,
  },
  passengersDisabledCount: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["close", "updated"]);

const travelStore = useTravelStore();
const vehicleStore = useVehicleStore();
const driverStore = useDriverStore();

const availableVehicles = ref([]);
const availableDrivers = ref([]);
const selectedVehicle = ref(null);
const selectedDriver = ref(null);

const isLoadingVehicles = ref(false);
const isLoadingDrivers = ref(false);
const isSubmitting = ref(false);

const overlapingTravels = ref(null);
const submitError = ref(null);

const loadDriversForVehicle = async (vehicleId) => {
  isLoadingDrivers.value = true;
  try {
    availableDrivers.value = await driverStore.checkDriversAvailability({
      date: props.date,
      selectedVehicle: vehicleId,
    });
  } finally {
    isLoadingDrivers.value = false;
  }
};

onMounted(async () => {
  try {
    isLoadingVehicles.value = true;
    availableVehicles.value = await vehicleStore.checkVehicleAvailability({
      date: props.date,
      passengersRegularCount: props.passengersRegularCount,
      passengersDisabledCount: props.passengersDisabledCount,
    });
    isLoadingVehicles.value = false;

    selectedVehicle.value =
      availableVehicles.value.find(
        (vehicle) => vehicle.id === props.vehicleId,
      ) || { id: props.vehicleId };

    await loadDriversForVehicle(selectedVehicle.value.id);

    selectedDriver.value =
      availableDrivers.value.find((driver) => driver.id === props.driverId) ||
      { id: props.driverId };
  } catch (err) {
    isLoadingVehicles.value = false;
    submitError.value =
      err.response?.data?.message ||
      "Nepavyko įkelti laisvų transporto priemonių ir vairuotojų";
  }
});

const handleVehicleSelection = async (vehicle) => {
  if (selectedVehicle.value?.id === vehicle.id) return;

  selectedVehicle.value = vehicle;
  selectedDriver.value = null;
  overlapingTravels.value = null;
  submitError.value = null;

  await loadDriversForVehicle(vehicle.id);
};

const handleDriverSelection = (driver) => {
  selectedDriver.value = driver;
  overlapingTravels.value = null;
  submitError.value = null;
};

const submit = async () => {
  if (!selectedVehicle.value?.id || !selectedDriver.value?.id) return;

  isSubmitting.value = true;
  submitError.value = null;
  overlapingTravels.value = null;

  try {
    overlapingTravels.value = await travelStore.checkTravelsOverlap({
      date: props.date,
      driverId: selectedDriver.value.id,
      vehicleId: selectedVehicle.value.id,
      startTime: props.startTime.slice(0, 5),
      finishTime: props.finishTime.slice(0, 5),
      excludeTravelId: props.travelId,
    });

    if (overlapingTravels.value.hasConflict) {
      return;
    }

    await travelStore.reassignDriverVehicle(props.travelId, {
      vehicle_id: selectedVehicle.value.id,
      driver_id: selectedDriver.value.id,
    });

    emit("updated");
  } catch (err) {
    submitError.value =
      err.response?.data?.message || "Nepavyko atnaujinti kelionės";
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit("close");
};
</script>
